import { Routes } from '@angular/router';

import { LeafEmptyRouteComponent } from './leaf-empty-route.component';

/**
 * Deriva o espelho `dss/{path}` de cada rota concreta, reutilizando a mesma
 * referência de `loadChildren`. Este router usa `useHash: true` e lê o mesmo
 * `window.location.hash` do shell React — sem os espelhos, uma rota `dss/home`
 * cai no catch-all porque só existe a entrada `home`.
 */
function buildDssMirroredRoutes(routes: Routes): Routes {
  return routes.map(({ path, loadChildren }) => ({ path: `dss/${path}`, loadChildren }));
}

const LEAF_CONCRETE_ROUTES: Routes = [
  {
    path: 'versao-app-loja',
    loadChildren: () =>
      import('app/pages/full-pages/versao-app-loja/versao-app-loja.module').then(
        m => m.VersaoAppLojaModule,
      ),
  },
  {
    path: 'gestao-pre-cadastros',
    loadChildren: () =>
      import('app/pages/full-pages/gestao-pre-cadastros/gestao-pre-cadastros.module').then(
        m => m.GestaoPreCadastrosModule,
      ),
  },
  {
    path: 'timeline-eventos',
    loadChildren: () =>
      import('app/pages/full-pages/timeline-eventos/timeline-eventos.module').then(
        m => m.TimeLineEventosModules,
      ),
  },
  {
    path: 'configmodulos',
    loadChildren: () =>
      import('app/pages/full-pages/configmodulos/configmodulos.module').then(
        m => m.ConfigModulosModule,
      ),
  },
  {
    path: 'migracao',
    loadChildren: () =>
      import('app/pages/full-pages/migracao/migracao.module').then(
        m => m.MigracaoModule,
      ),
  },
  {
    path: 'home',
    loadChildren: () =>
      import('app/pages/full-pages/home/home.module').then(
        m => m.HomeModule,
      ),
  },
  {
    path: 'contrato',
    loadChildren: () =>
      import('app/pages/full-pages/contrato/contrato.module').then(
        m => m.ContratoModule,
      ),
  },
  {
    path: 'config-bi-multiconta',
    loadChildren: () =>
      import('app/pages/full-pages/configbimulticonta/config-bi-multiconta.module').then(
        m => m.ConfigBIMultiContaModule,
      ),
  },
  {
    path: 'log-webhook',
    loadChildren: () =>
      import('app/pages/full-pages/log-webhook/log-webhook.module').then(
        m => m.LogWebhookModule,
      ),
  },
  {
    path: 'log-consulta-erro',
    loadChildren: () =>
      import('app/pages/full-pages/log-consulta-error/log-consulta-erro.module').then(
        m => m.LogConsultaErroModule,
      ),
  },
  {
    path: 'configuracoes',
    loadChildren: () =>
      import('app/pages/full-pages/configuracoes/configuracoes.module').then(
        m => m.ConfiguracoesModule,
      ),
  },
  {
    path: 'evento',
    loadChildren: () =>
      import('app/pages/full-pages/evento/evento.module').then(
        m => m.EventoModule,
      ),
  },
  {
    path: 'portaria-funcionario',
    loadChildren: () =>
      import('app/pages/full-pages/portaria-funcionario/portaria-funcionario.module').then(
        m => m.PortariaFuncionarioModule,
      ),
  },
  {
    // Path de dois segmentos: a rota real vive sob content-pages, não full-pages,
    // mas essa tabela é independente de ambas — o literal reflete o hash real.
    path: 'pages/softphone',
    loadChildren: () =>
      import('app/pages/full-pages/softphone/softphone.module').then(
        m => m.SoftphoneModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-sub-integrador` e suas três filhas
    // continuam declaradas exclusivamente em SubIntegradorRoutingModule.
    path: 'sub-integrador',
    loadChildren: () =>
      import('app/pages/full-pages/sub-integrador/sub-integrador.module').then(
        m => m.SubIntegradorModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-perfil-acesso` e suas filhas
    // (incluindo `add-perfil-acesso/:paid`) continuam declaradas exclusivamente
    // em PerfilAcessoRoutingModule.
    path: 'perfil-acesso',
    loadChildren: () =>
      import('app/pages/full-pages/perfil-acesso/perfil-acesso.module').then(
        m => m.PerfilAcessoModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-horarios` e suas três filhas
    // (incluindo `add-horarios/:nhid`) continuam declaradas exclusivamente
    // em HorariosRoutingModule.
    path: 'horarios',
    loadChildren: () =>
      import('app/pages/full-pages/horarios/horarios.module').then(
        m => m.HorariosModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-inquilino` e suas quatro filhas
    // (incluindo `add-inquilino/:id` e `visualizar-inquilino/:id`) continuam
    // declaradas exclusivamente em InquilinoRoutingModule.
    path: 'inquilino',
    loadChildren: () =>
      import('app/pages/full-pages/inquilino/inquilino.module').then(
        m => m.InquilinoModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-estacionamento` e suas quatro
    // filhas (incluindo `add-estacionamento/:eid`) continuam declaradas
    // exclusivamente em EstacionamentoRoutingModule.
    path: 'estacionamento',
    loadChildren: () =>
      import('app/pages/full-pages/estacionamento/estacionamento.module').then(
        m => m.EstacionamentoModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-vaga/:eid` e suas quatro filhas
    // (`''`, o par `add-vaga`/`add-vaga/:vid` e `vaga-cadastro-automatico/:eid`)
    // continuam declaradas exclusivamente em VagaRoutingModule.
    //
    // Destino de navegação de `list-estacionamento`, que é servido pela folha:
    // sem esta entrada o clique em "vagas" cairia no catch-all vazio.
    path: 'vaga',
    loadChildren: () =>
      import('app/pages/full-pages/vaga/vaga.module').then(
        m => m.VagaModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-fornecedor` e suas quatro
    // filhas (incluindo `add-fornecedor/:id/:checkId`) continuam declaradas
    // exclusivamente em FornecedorRoutingModule.
    path: 'fornecedor',
    loadChildren: () =>
      import('app/pages/full-pages/fornecedor/fornecedor.module').then(
        m => m.FornecedorModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-documentos` e suas quatro
    // filhas (incluindo `add-documento/:id` e `view-documento/:id`) continuam
    // declaradas exclusivamente em DocumentosRoutingModule.
    path: 'documento',
    loadChildren: () =>
      import('app/pages/full-pages/documentos/documentos.module').then(
        m => m.DocumentosModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-categoria` e suas três filhas
    // (`''` e o par `add-categoria`/`add-categoria/:id`) continuam declaradas
    // exclusivamente em CategoriasRoutingModule.
    //
    // CategoriasModule mora na pasta de `documentos` mas responde por um
    // segmento de topo próprio — é o que fez a rota passar batido na migração
    // de `documento`, deixando o botão "Categorias" em tela vazia.
    path: 'categoria',
    loadChildren: () =>
      import('app/pages/full-pages/documentos/categorias/categorias.module').then(
        m => m.CategoriasModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-credenciais` (com suas duas
    // filhas) e a rota irmã flat `vincular-credencial` continuam declaradas
    // exclusivamente em CredencialRoutingModule.
    path: 'credencial',
    loadChildren: () =>
      import('app/pages/full-pages/credencial/credencial.module').then(
        m => m.CredencialModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-cftv` e suas duas filhas
    // (`add-cftv` e `add-cftv/:id`) continuam declaradas exclusivamente em
    // CftvRoutingModule.
    path: 'cftv',
    loadChildren: () =>
      import('app/pages/full-pages/cftv/cftv.module').then(
        m => m.CftvModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-manutencao-preventiva` e suas
    // duas filhas (`add-manutencao-preventiva` e
    // `add-manutencao-preventiva/:id/:checkId`) continuam declaradas
    // exclusivamente em ManutencaoPreventivaRoutingModule.
    path: 'manutencao-preventiva',
    loadChildren: () =>
      import('app/pages/full-pages/manutencao-preventiva/manutencao-preventiva.module').then(
        m => m.ManutencaoPreventivaModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `''` (sem canActivate) e suas quatro
    // filhas (`''`, o par `add-dispositivo`/`add-dispositivo/:id` e
    // `dispositivo/:id`) continuam declaradas exclusivamente em
    // ZeladorIaRoutingModule.
    path: 'zelador-ia',
    loadChildren: () =>
      import('app/pages/full-pages/zelador-ia/zelador-ia.module').then(
        m => m.ZeladorIaModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-portaria` e suas duas filhas
    // aninhadas (`''` e `add-portaria`/`add-portaria/:id`) continuam
    // declaradas exclusivamente em PortariaRoutingModule.
    path: 'portaria',
    loadChildren: () =>
      import('app/pages/full-pages/portaria/portaria.module').then(
        m => m.PortariaModule,
      ),
  },
  {
    // Só o segmento de topo: as duas rotas-pai irmãs `list-log-notificacao` e
    // `list-log-dispositivos` (com a filha de detalhe reusada entre ambas)
    // continuam declaradas exclusivamente em LogNotificacaoRoutingModule.
    path: 'log-notificacao',
    loadChildren: () =>
      import('app/pages/full-pages/log-notificacao/log-notificacao.module').then(
        m => m.LogNotificacaoModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `conta` e suas quatro filhas flat
    // (`select-conta`, `manage-conta`, `migrar-conta`, `list-conta`) continuam
    // declaradas exclusivamente em ContaRoutingModule.
    path: 'conta',
    loadChildren: () =>
      import('app/pages/full-pages/conta/conta.module').then(
        m => m.ContaModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-notificacao-tipo-evento/:id` e
    // suas duas filhas aninhadas continuam declaradas exclusivamente em
    // NotificacaoTipoEventoRoutingModule. Path diverge da pasta real do
    // módulo (tipoeventopsim/).
    path: 'notificacao-tipo-evento',
    loadChildren: () =>
      import('app/pages/full-pages/tipoeventopsim/notificacao-tipo-evento.module').then(
        m => m.NotificacaoTipoEventoModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-configcameras/:id` (com
    // canActivate: [TenantGuard]) e suas duas filhas aninhadas continuam
    // declaradas exclusivamente em ConfigCameraRoutingModule. Path diverge da
    // pasta real do módulo (configcameras/, plural, sem hífen) e do nome da
    // classe (ConfigCameraModule, singular).
    path: 'config-cameras',
    loadChildren: () =>
      import('app/pages/full-pages/configcameras/configcameras.module').then(
        m => m.ConfigCameraModule,
      ),
  },
  {
    // Só o segmento de topo: as duas rotas-pai irmãs `list-cameras/:id` (com
    // canActivate: [TenantGuard]) e `list-cameras-menu` (sem guard), a rota
    // irmã flat `lpr-cameras/:id` (também com TenantGuard), e as duas filhas
    // homônimas `configurar-list-cameras` continuam declaradas exclusivamente
    // em CamerasRoutingModule.
    path: 'cameras',
    loadChildren: () =>
      import('app/pages/full-pages/cameras/cameras.module').then(
        m => m.CamerasModule,
      ),
  },
  {
    // Só o segmento de topo: as duas rotas-pai irmãs `list-key/:id` e
    // `list-config-zonas/:id` (ambas com canActivate: [TenantGuard]), e a
    // filha `configuracoes-zona-porta/:id` reusada sob as duas, continuam
    // declaradas exclusivamente em ConfigScondKeyRoutingModule.
    path: 'config-scond',
    loadChildren: () =>
      import('app/pages/full-pages/configscondkey/configscondkey.module').then(
        m => m.ConfigScondKeyModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-agendamento` (sem canActivate)
    // e suas cinco filhas (`''`, `:aid`, e as três variantes de
    // `confirmar-agendamento-component`) continuam declaradas exclusivamente
    // em AgendamentoRoutingModule.
    path: 'agendamento',
    loadChildren: () =>
      import('app/pages/full-pages/agendamento/agendamento.module').then(
        m => m.AgendamentoModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-lista-convidados` e suas três
    // filhas (`''` e o par `add-lista-convidados`/`add-lista-convidados/:id`)
    // continuam declaradas exclusivamente em ListaConvidadosRoutingModule.
    //
    // Destino de navegação de `confirmar-agendamento` e de `container-list-portaria`,
    // ambos servidos pela folha. O segundo contornava a ausência desta entrada
    // com uma travessia manual da fronteira React.
    path: 'lista-convidados',
    loadChildren: () =>
      import('app/pages/full-pages/lista-convidados/lista-convidados.module').then(
        m => m.ListaConvidadosModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-area-comum` (sem canActivate) e
    // suas cinco filhas (`''`, e os dois pares `add-area-comum`/`:aid` e
    // `step-add-area-comum`/`:aid`) continuam declaradas exclusivamente em
    // AreaComumRoutingModule.
    path: 'area-comum',
    loadChildren: () =>
      import('app/pages/full-pages/area-comum/area-comum.module').then(
        m => m.AreaComumModule,
      ),
  },
  {
    // Só o segmento de topo: as duas rotas-pai irmãs `list-lgpd-termos-uso` e
    // `list-lgpd-politica-privacidade` (sem canActivate), cada uma com as
    // mesmas três filhas reusando os mesmos dois componentes, continuam
    // declaradas exclusivamente em LgpdRoutingModule (arquivo com nome
    // invertido, lgpd.module.routing.ts).
    path: 'lgpd',
    loadChildren: () =>
      import('app/pages/full-pages/lgpd/lgpd.module').then(
        m => m.LgpdModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-grupo-pessoa` (sem canActivate)
    // e suas seis filhas (`''`, os dois pares `add-grupo-pessoa`/`:id` e as
    // três variantes de `add-permissao-pessoa`, incluindo a barra final
    // literal) continuam declaradas exclusivamente em
    // GrupoPessoaRoutingModule.
    path: 'grupo-pessoa',
    loadChildren: () =>
      import('app/pages/full-pages/grupo-pessoa/grupo-pessoa.module').then(
        m => m.GrupoPessoaModule,
      ),
  },
  {
    // Só o segmento de topo: as duas rotas-pai irmãs `list-categoria-chamado`
    // e `list-chamado` (sem canActivate), cada uma com suas próprias três
    // filhas — sem nenhum componente compartilhado entre as duas famílias —
    // continuam declaradas exclusivamente em ChamadoRoutingModule.
    path: 'chamado',
    loadChildren: () =>
      import('app/pages/full-pages/chamado/chamado.module').then(
        m => m.ChamadoModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-enquetes` (plural, sem
    // canActivate) e suas sete filhas — três pares reusando o mesmo
    // componente entre a variante sem `:id` e a variante com `:id`, mais a
    // filha raiz da listagem — continuam declaradas exclusivamente em
    // EnqueteRoutingModule. Pasta/prefixo do módulo é `enquete` (singular).
    path: 'enquete',
    loadChildren: () =>
      import('app/pages/full-pages/enquete/enquete.module').then(
        m => m.EnqueteModule,
      ),
  },
  {
    // Só o segmento de topo: as duas rotas-pai irmãs `list-dispositivo-sip` e
    // `list-tronco` (sem canActivate), cada uma com suas próprias três
    // filhas sem componente compartilhado entre as duas famílias, mais a
    // rota irmã flat `config-voip` (component: direto, sem wrapper nem
    // children) continuam declaradas exclusivamente em VoipRoutingModule.
    path: 'voip',
    loadChildren: () =>
      import('app/pages/full-pages/voip/voip.module').then(
        m => m.VoipModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-recado` (sem canActivate) e
    // suas sete filhas — três pares reusando o mesmo componente entre a
    // variante sem `:rid` e a variante com `:rid`, mais a filha raiz da
    // listagem — continuam declaradas exclusivamente em
    // RecadoRoutingModule.
    path: 'recado',
    loadChildren: () =>
      import('app/pages/full-pages/recado/recado.module').then(
        m => m.RecadoModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-assembleia` (sem canActivate) e
    // suas sete filhas irmãs continuam declaradas exclusivamente em
    // AssembleiaRoutingModule. Atenção: o segmento `assembleia` se repete
    // três vezes no path composto das rotas intermediárias (este prefixo +
    // o wrapper `list-assembleia` + o primeiro segmento da filha
    // `assembleia/:id/...`) — nenhuma das três ocorrências é redundante.
    path: 'assembleia',
    loadChildren: () =>
      import('app/pages/full-pages/assembleia/assembleia.module').then(
        m => m.AssembleiaModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-correspondencias` (sem
    // canActivate), suas sete filhas — incluindo o reuso de
    // AddCorrespondenciaComponent entre `add-correspondencias` e
    // `entregar-component` sem `:cid` — e a rota irmã flat
    // `list-correspondencias-unidade` continuam declaradas exclusivamente em
    // CorrespondenciaRoutingModule.
    path: 'correspondencia',
    loadChildren: () =>
      import('app/pages/full-pages/correspondencia/correspondencia.module').then(
        m => m.CorrespondenciaModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-pessoa` (sem canActivate) e
    // suas sete filhas — incluindo as quatro que roteiam componentes de
    // MeusDadosModule sem que PessoaModule importe esse módulo — continuam
    // declaradas exclusivamente em PessoaRoutingModule.
    path: 'pessoa',
    loadChildren: () =>
      import('app/pages/full-pages/pessoa/pessoa.module').then(
        m => m.PessoaModule,
      ),
  },
  {
    // Só o segmento de topo: as treze rotas flat irmãs (sem canActivate,
    // sem parâmetro, sem children) continuam declaradas exclusivamente em
    // RelatoriosRoutingModule. Path singular (relatorio) diverge da pasta
    // real do módulo (relatorios/, plural) e dos nomes de classe
    // RelatoriosModule/RelatoriosRoutingModule (ambos plural) — reproduzido
    // exatamente como registrado hoje em full-pages-routing.module.ts.
    path: 'relatorio',
    loadChildren: () =>
      import('app/pages/full-pages/relatorios/relatorios.module').then(
        m => m.RelatoriosModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-log-operacoes` (sem
    // canActivate) e suas onze filhas parametrizadas
    // (`detalhes-*/:idRev/:idEntidade/:entidade`) continuam declaradas
    // exclusivamente em LogOperacoesRoutingModule.
    path: 'log-operacoes',
    loadChildren: () =>
      import('app/pages/full-pages/log-operacoes/log-operacoes.module').then(
        m => m.LogOperacoesModule,
      ),
  },
  {
    // Só o segmento de topo: as três rotas-pai irmãs `app-locker`,
    // `list-biccateca` e `list-entrega-autonoma` (sem canActivate em nenhuma
    // delas nem em suas filhas) continuam declaradas exclusivamente em
    // LockerRoutingModule — incluindo o reuso de AddBiccatecaComponent entre
    // app-locker e list-biccateca, dois pais irmãos distintos.
    path: 'locker',
    loadChildren: () =>
      import('app/pages/full-pages/locker/locker.module').then(
        m => m.LockerModule,
      ),
  },
  {
    // Só o segmento de topo: as quatro rotas-pai irmãs `configpsim-conexao/:id`
    // e `configpsim-gerais` (sem canActivate) e `configpsim-gerais/:id` e
    // `step-configpsim/:id` (ambas com canActivate: [TenantGuard]) — incluindo
    // o reuso assimétrico de gating de ConfigPSIMGeraisComponent entre as três
    // rotas irmãs/herdadas que o montam — continuam declaradas exclusivamente
    // em ConfigPSIMRoutingModule. Path diverge da pasta real do módulo
    // (configpsim/, sem hífen).
    path: 'config-psim',
    loadChildren: () =>
      import('app/pages/full-pages/configpsim/configpsim.module').then(
        m => m.ConfigPSIMModule,
      ),
  },
  {
    // Só o segmento de topo: a árvore duplicada 'list-equipamento' (sem
    // canActivate) e 'list-equipamento/:id' (com canActivate: [TenantGuard],
    // herdado pelas três filhas) — os mesmos dois componentes montados sob
    // os dois regimes de gating —, incluindo o reuso de ListEquipamentoComponent
    // e AddEquipamentoComponent entre as duas árvores, e a rota irmã flat
    // 'porta-divisao-area' continuam declaradas exclusivamente em
    // EquipamentoRoutingModule. Typo real na classe (EquiamentoModule,
    // faltando o "p") replicado aqui exatamente como declarado em
    // full-pages-routing.module.ts.
    path: 'equipamento',
    loadChildren: () =>
      import('app/pages/full-pages/equipamento/equipamento.module').then(
        m => m.EquiamentoModule,
      ),
  },
  {
    // Só o segmento de topo: a árvore duplicada 'list-equipamento-hub' (sem
    // canActivate) e 'list-equipamento-hub/:id' (com canActivate: [TenantGuard],
    // herdado pelas três filhas); a rota-pai irmã 'porta-hub' e suas duas filhas
    // cross-module (configuracoes-porta/:pid e configuracoes-zona-porta/:id,
    // ambas declaradas em ConfigScondKeyModule, roteadas aqui sem que HubModule
    // importe aquele módulo); e os três wrappers de filha única
    // (status-equipamento-hub, log-eventos-hub, log-sincronizacao-hub) continuam
    // declarados exclusivamente em HubRoutingModule.
    path: 'hub',
    loadChildren: () =>
      import('app/pages/full-pages/hub/hub.module').then(
        m => m.HubModule,
      ),
  },
  {
    // Só o segmento de topo: o aninhamento de quatro níveis sob 'list-unidade'
    // (com :uid herdado por rotas componentless encadeadas até
    // 'vincular-pessoa-unidade/add-pessoa/:pid'), as duas rotas flat
    // 'cadastro-automatico/:id' e 'divisoes/:id' (ambas com canActivate:
    // [TenantGuard]), e as quatro rotas que roteiam ContainerAddPessoaComponent
    // de PessoaModule sem que UnidadeModule importe aquele módulo continuam
    // declaradas exclusivamente em UnidadeRoutingModule.
    path: 'unidade',
    loadChildren: () =>
      import('app/pages/full-pages/unidade/unidade.module').then(
        m => m.UnidadeModule,
      ),
  },
  {
    // Só o segmento de topo: o redirect de raiz, a árvore 'sms-view'
    // duplicada (com :id herdado por dois níveis de rota componentless na
    // variante aninhada), o reuso de CustomSkinComponent, e o loadChildren
    // duplamente aninhado do submódulo interno de 'recado' (mesmo nome de
    // classe do RecadoModule de topo, sem import cruzado) continuam
    // declarados exclusivamente em IntegradorRoutingModule e no
    // RecadoRoutingModule interno.
    path: 'integrador',
    loadChildren: () =>
      import('app/pages/full-pages/integrador/integrador.module').then(
        m => m.IntegradorModule,
      ),
  },
  {
    // Só o segmento de topo: a rota `''` que monta MeusDadosComponent continua
    // declarada exclusivamente em MeusDadosRoutingModule.
    path: 'meus-dados',
    loadChildren: () =>
      import('app/pages/full-pages/meus-dados/meus-dados.module').then(
        m => m.MeusDadosModule,
      ),
  },
  {
    // Só o segmento de topo: a rota `''` que monta AlterarSenhaComponent
    // continua declarada exclusivamente em AdminRoutingModule. O path
    // ('alterar-senha') diverge da pasta e da classe do módulo (admin/,
    // AdminModule) — reproduzido como já está registrado hoje.
    path: 'alterar-senha',
    loadChildren: () =>
      import('app/pages/full-pages/admin/admin.module').then(
        m => m.AdminModule,
      ),
  },
  {
    path: 'equipamento-pac',
    loadChildren: () =>
      import('app/pages/full-pages/equipamento-pac/equipamento-pac.module').then(
        m => m.EquipamentoPacModule,
      ),
  },
  {
    path: 'convite-virtual',
    loadChildren: () =>
      import('app/pages/full-pages/convite-virtual/convite-virtual.module').then(
        m => m.ConviteVirtualModule,
      ),
  },
  {
    path: 'chave-virtual',
    loadChildren: () =>
      import('app/pages/full-pages/chave-virtual-acesso/chave-virtual-acesso.module').then(
        m => m.ChaveVirtualAcessoModule,
      ),
  },
  {
    // Só o segmento de topo: as três rotas (`''`, `preCadastro/:pcid` e
    // `gestao/preCadastro/:pcid`, todas montando WizardComponent) continuam
    // declaradas exclusivamente em WizardRoutingModule. Pasta com typo
    // (wirzard/). O mesmo WizardModule também é roteado por
    // `pages/preCadastro` em ContentPagesRoutingModule — aquele prefixo é
    // público e segue no AngularHost, então o módulo passa a ser instanciado
    // nos dois injectors.
    path: 'wizard',
    loadChildren: () =>
      import('app/pages/full-pages/wirzard/wizard.module').then(
        m => m.WizardModule,
      ),
  },
  {
    // Só o segmento de topo: a rota `list-pre-cadastro-component` continua
    // declarada exclusivamente em PreCadastroRoutingModule. Entra junto com
    // `wizard` porque os dois se navegam mutuamente (a listagem abre
    // `/wizard/preCadastro/:uuid`, e aprovar/reprovar fora do modo gestão
    // volta para cá); registrar só um deixaria a volta fora desta tabela.
    path: 'pre-cadastro',
    loadChildren: () =>
      import('app/pages/full-pages/pre-cadastro/pre-cadastro.module').then(
        m => m.PreCadastroModule,
      ),
  },
  {
    // Só o segmento de topo: a rota-pai `list-modo-debug` (sem canActivate) e
    // suas três filhas (`''`, `buscar` e `buscar/:id`) continuam declaradas
    // exclusivamente em ModoDebugRoutingModule. O service AbModoDebug vive em
    // integrador/modo-debug/, fora da pasta do módulo, e chega pelo AbstractModule.
    path: 'modo-debug',
    loadChildren: () =>
      import('app/pages/full-pages/modo-debug/modo-debug.module').then(
        m => m.ModoDebugModule,
      ),
  },
];

/**
 * Tabela chrome-less do roteador da folha: espelha as rotas de full-pages das
 * features migradas, sem o FullLayoutComponent. Ponto único de registro do lado
 * Angular — cada módulo migrado ganha uma entrada aqui, correspondendo à mesma
 * feature declarada em `full-pages-routing.module.ts`.
 *
 * Inclui também o espelho `dss/{path}` de cada entrada acima: o shell React
 * monta este mesmo router (via `AngularLeafHost`) tanto para rotas `/{path}`
 * quanto para `/dss/{path}`, e o hash chega idêntico em ambos os casos.
 *
 * Catch-all no final: rota fora deste registro resulta em outlet vazio (falha
 * segura), nunca redirecionar para telas fora da folha.
 */
export const LEAF_ROUTES: Routes = [
  ...LEAF_CONCRETE_ROUTES,
  ...buildDssMirroredRoutes(LEAF_CONCRETE_ROUTES),
  { path: '**', component: LeafEmptyRouteComponent },
];
